// Fully custom "Flocage" personalization block — works on every product without any
// Shopify variant/option. Handles: revealing the Nom/Numéro fields, sanitizing their
// input, keeping the displayed price + "Ajouter au panier" button in sync with the
// (+4,99€) surcharge, and auto-adding a store-wide "Flocage personnalisé" line item to
// the cart (linked to the jersey line via a hidden _flocage_link property) so the
// surcharge is genuinely charged at checkout. Removing the fee line when its paired
// jersey line is removed is handled separately by cart-flocage-sync.js.
function generateFlocageLinkId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `flocage-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

if (!customElements.get('product-personalization')) {
  customElements.define(
    'product-personalization',
    class ProductPersonalization extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        this.feeVariantId = this.dataset.feeVariantId || '';

        this.radios = Array.from(this.querySelectorAll('[data-flocage-radio]'));
        this.fieldsWrapper = this.querySelector('.product-personalization__fields');
        this.inputs = Array.from(this.querySelectorAll('.field__input'));
        this.nameInput = this.querySelector('[data-personalization-name]');
        this.numberInput = this.querySelector('[data-personalization-number]');
        this.flocagePropertyInput = this.querySelector('[data-flocage-property]');
        this.linkInput = this.querySelector('[data-flocage-link]');

        const priceMapScript = this.querySelector('[data-personalization-price-map]');
        this.priceMap = priceMapScript ? JSON.parse(priceMapScript.textContent) : {};

        this.priceBlock = (this.closest('product-info') || document).querySelector('[id^="price-"]');
        this.variantIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;

        this.radios.forEach((radio) => radio.addEventListener('change', this.sync.bind(this)));

        if (this.nameInput) this.nameInput.addEventListener('input', this.sanitizeName.bind(this));
        if (this.numberInput) this.numberInput.addEventListener('input', this.sanitizeNumber.bind(this));

        if (this.priceBlock) {
          this.priceObserver = new MutationObserver(this.applyPrice.bind(this));
          this.priceObserver.observe(this.priceBlock, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.pendingSubmission = null;
        if (this.form) {
          this.form.addEventListener('submit', this.onFormSubmit.bind(this));
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
          this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, () => {
            this.pendingSubmission = null;
          });
        }

        this.sync();
      }

      disconnectedCallback() {
        this.priceObserver?.disconnect();
        this.unsubscribeCartUpdate?.();
        this.unsubscribeCartError?.();
      }

      isFlocageSelected() {
        const checked = this.radios.find((radio) => radio.checked);
        return Boolean(checked && checked.value === 'with');
      }

      sync() {
        const selected = this.isFlocageSelected();

        if (this.fieldsWrapper) this.fieldsWrapper.hidden = !selected;

        this.inputs.forEach((input) => {
          if (!selected) input.value = '';
        });

        if (this.flocagePropertyInput) {
          this.flocagePropertyInput.value = selected ? 'Oui' : 'Non';
        }

        // Generate the pairing id as soon as "Avec flocage" is picked — NOT at
        // submit time. Dawn's own product-form submit listener is registered
        // before ours (product-form.js loads first), so it reads the form's
        // FormData before an id set inside our own submit handler would land,
        // shipping the jersey line with an empty _flocage_link. Setting it
        // here means it's already sitting in the DOM well before any submit.
        if (this.linkInput) {
          this.linkInput.value = selected ? generateFlocageLinkId() : '';
        }

        this.applyPrice();
      }

      applyPrice() {
        if (!this.priceBlock || !this.variantIdInput) return;

        const entry = this.priceMap[this.variantIdInput.value];
        if (!entry) return;

        const target = this.isFlocageSelected() ? entry.withFlocage : entry.base;

        const priceRoot = this.priceBlock.querySelector('.price');
        const isOnSale = Boolean(priceRoot && priceRoot.classList.contains('price--on-sale'));
        const saleEl = this.priceBlock.querySelector('.price__sale .price-item--last');
        const regularEl = this.priceBlock.querySelector('.price__regular .price-item--regular');

        this.priceObserver?.disconnect();
        if (saleEl) saleEl.textContent = target;
        if (!isOnSale && regularEl) regularEl.textContent = target;
        if (this.priceBlock) {
          this.priceObserver?.observe(this.priceBlock, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      sanitizeName(event) {
        const input = event.target;
        const cursor = input.selectionStart;
        const before = input.value.length;
        // Letters (incl. accents) and spaces only, forced uppercase
        input.value = input.value
          .toUpperCase()
          .replace(/[^A-ZÀ-Ÿ ]/g, '')
          .slice(0, input.maxLength > 0 ? input.maxLength : undefined);
        const removed = before - input.value.length;
        if (cursor !== null) {
          const newPos = Math.max(0, cursor - removed);
          input.setSelectionRange(newPos, newPos);
        }
      }

      sanitizeNumber(event) {
        const input = event.target;
        let digits = input.value.replace(/\D/g, '').slice(0, 2);
        if (digits !== '') {
          // Only 1–99 is allowed: clamp above 99, drop an entered 0 (not a valid number)
          let numeric = Math.min(99, parseInt(digits, 10));
          digits = numeric === 0 ? '' : String(numeric);
        }
        input.value = digits;
      }

      onFormSubmit() {
        if (!this.isFlocageSelected()) {
          this.pendingSubmission = null;
          return;
        }

        // The link id was already generated and written to the DOM by sync()
        // when "Avec flocage" was selected — just read it back here. Fall
        // back to a fresh one only in the unlikely case it's still empty.
        let linkId = this.linkInput ? this.linkInput.value : '';
        if (!linkId) {
          linkId = generateFlocageLinkId();
          if (this.linkInput) this.linkInput.value = linkId;
        }

        const quantityInput = this.form.querySelector('input[name="quantity"]');
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

        this.pendingSubmission = {
          quantity,
          linkId,
          mainVariantId: this.variantIdInput ? this.variantIdInput.value : null,
        };
      }

      onCartUpdate(event) {
        const submission = this.pendingSubmission;
        if (!submission) return;
        this.pendingSubmission = null;

        if (submission.mainVariantId && event?.productVariantId && String(event.productVariantId) !== String(submission.mainVariantId)) {
          return;
        }

        this.addFeeToCart(submission);
      }

      addFeeToCart({ quantity, linkId }) {
        if (!this.feeVariantId) {
          console.warn(
            'FootKit : le produit "Flocage personnalisé" (handle "flocage-personnalise") est introuvable ou indisponible — le supplément flocage ne sera pas facturé au panier.'
          );
          return;
        }

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const config = fetchConfig('json');
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', this.feeVariantId);
        formData.append('quantity', quantity);
        formData.append('properties[_flocage_link]', linkId);
        formData.append('properties[_flocage_fee]', 'true');
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error('FootKit : échec de l’ajout du supplément flocage au panier', response);
              return;
            }
            if (cart) cart.renderContents(response);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-personalization', cartData: response });
            }
          })
          .catch((error) => {
            console.error('FootKit : erreur réseau lors de l’ajout du supplément flocage', error);
          });
      }
    }
  );
}
